/* Akaari India -> USA. A browser redirect is regional routing, not an SEO migration. */
(() => {
  'use strict';

  const script = document.getElementById('akaari-us-redirect');
  if (!script || window.Shopify?.designMode || window.self !== window.top) return;
  if (typeof fetch !== 'function' || typeof AbortController !== 'function') return;
  const current = new URL(window.location.href);
  if (!['shopakaari.in', 'www.shopakaari.in'].includes(current.hostname)) return;
  if ([...current.searchParams.keys()].some((key) => /^(preview_theme_id|_fd|pb|preview_key)$/i.test(key))) return;
  if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkout|checkouts|account|orders|challenge|password|apps|tools)(?:\/|$)/i.test(current.pathname)) return;
  if (Number(script.dataset.cartCount) > 0) return;

  // This recognises common crawlers, not every crawler. It is not verified bot identity.
  if (/bot|crawler|spider|slurp|googleother|google-inspectiontool|lighthouse|pagespeed|mediapartners|facebookexternalhit/i.test(navigator.userAgent)) return;

  const choiceKey = 'akaari-india-choice-v1';
  const sessionKey = 'akaari-us-redirect-session-v1';
  const countryKey = 'akaari-detected-country-v1';
  const choiceDuration = 30 * 24 * 60 * 60 * 1000;
  const countryDuration = 15 * 60 * 1000;
  const read = (storage, key) => {
    try { return JSON.parse(window[storage].getItem(key)); } catch { return null; }
  };
  const write = (storage, key, value) => {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch { /* Storage is optional. */ }
  };
  const stayInIndia = () => {
    write('localStorage', choiceKey, { expires: Date.now() + choiceDuration });
    write('sessionStorage', sessionKey, 'stay');
  };

  if (current.searchParams.get('akaari_region')?.toLowerCase() === 'in') {
    stayInIndia();
    return;
  }
  if (read('localStorage', choiceKey)?.expires > Date.now() || read('sessionStorage', sessionKey)) return;
  try {
    // Existing navigation and a return from the USA store should never become a redirect loop.
    const referrer = new URL(document.referrer);
    if (['shopakaari.in', 'www.shopakaari.in', 'shopakaari.com', 'www.shopakaari.com'].includes(referrer.hostname)) return;
  } catch { /* Direct visits have no referrer. */ }

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    write('sessionStorage', sessionKey, 'stay');
  };
  // Do not interrupt someone who has already started interacting with the India store.
  document.addEventListener('pointerdown', cancel, { once: true, capture: true });
  document.addEventListener('keydown', cancel, { once: true, capture: true });
  document.addEventListener('submit', cancel, { once: true, capture: true });
  const cleanup = () => {
    document.removeEventListener('pointerdown', cancel, true);
    document.removeEventListener('keydown', cancel, true);
    document.removeEventListener('submit', cancel, true);
  };

  const destination = () => {
    let target;
    try {
      target = new URL(script.dataset.destination);
      if (target.protocol !== 'https:' || !['shopakaari.com', 'www.shopakaari.com'].includes(target.hostname) || target.username || target.password || target.port) throw new Error('Invalid destination');
    } catch {
      target = new URL('https://shopakaari.com/');
    }
    // Variant IDs, cart IDs and private query values do not belong to the other store.
    target.search = '';
    target.hash = '';
    for (const [key, value] of current.searchParams) {
      if (/^(utm_(?:source|medium|campaign|term|content|id)|gclid|fbclid|msclkid)$/i.test(key)) target.searchParams.append(key, value);
    }
    return target.href;
  };

  const route = (country) => {
    cleanup();
    if (country !== 'US' || cancelled) return;
    write('sessionStorage', sessionKey, 'redirected');
    window.location.replace(destination());
  };
  const cached = read('sessionStorage', countryKey);
  if (cached?.expires > Date.now() && /^[A-Z]{2}$/.test(cached.country)) {
    route(cached.country);
    return;
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => { cancelled = true; controller.abort(); cleanup(); }, 2000);
  // This observed Shopify endpoint is not a versioned, documented public Ajax API.
  // Only detected_values is used; market suggestions can point at the India-only market.
  fetch('/browsing_context_suggestions.json', {
    credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
    headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) throw new Error('Country lookup failed');
    return response.json();
  }).then((data) => {
    const country = data?.detected_values?.country?.handle;
    if (!/^[A-Z]{2}$/.test(country)) return;
    write('sessionStorage', countryKey, { country, expires: Date.now() + countryDuration });
    route(country);
  }).catch(() => {
    // The India storefront keeps working on a timeout, denied request or response change.
  }).finally(() => {
    window.clearTimeout(timer);
    cleanup();
  });
})();
